import React from "react";
import { useTranslation } from "react-i18next";
import { ICreditor } from "us.common/components/MainSearch/Interfaces";
import { DateValues } from "us.common/components/MainSearch/Constants";
import {
  $Avatar,
  $Col,
  $Row,
  $Tag,
  $DateLabel,
  $Tooltip,
} from "us.common/components";
import { ResultView } from "./ResultView";
import { getRouteUrl } from "us.helper";
import { CrownOutlined } from "us.icons";
import { useNavigate } from "react-router-dom";
import _ from "lodash";
import dayjs from "dayjs";

/**
 * @description - Columns component for creditor result view.
 * @author Samitha hewawasam <samitha@unicorn-solutions.com>
 * @since 10/03/2022
 * */
export const Creditor: React.FC<ICreditor> = (props) => {
  const { creditorData, paginationChangeEvent, currentHit, totalSearchResult } =
    props;

  const { t } = useTranslation();
  const navigate = useNavigate();

  /**
   * Handle the row click event of dashboard view
   * @param e click event.
   * @param linkId navigation id.
   */
  const rowClick = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    linkId: number | string,
    path: "creditor" | "creditor-group"
  ) => {
    e.preventDefault();
    if (window.APP_MODULE == "collection") {
      if (e.ctrlKey) {
        getRouteUrl.moduleRoute("collection", `/${path}/${linkId}`);
      } else {
        navigate(`/${path}/${linkId}`);
      }
    } else {
      getRouteUrl.moduleRoute("collection", `/${path}/${linkId}`);
    }
  };

  const tableColumns: any = [
    {
      dataIndex: "type",
      key: "type",
      className: "item-type",
      width: "50px",
      render: (text: any, { isMaster }: any) => {
        return (
          <>
            {isMaster && (
              <$Tooltip
                title={t("US.COLLECTION.COMMON:COMMON.MASTER_CREDITOR")}
              >
                <$Avatar
                  shape="square"
                  size="small"
                  className="master-creditor"
                  icon={<CrownOutlined />}
                />
              </$Tooltip>
            )}
            {!isMaster && (
              <$Avatar shape="square" size="small">
                Cr
              </$Avatar>
            )}
          </>
        );
      },
    },
    {
      dataIndex: "result",
      key: "result",
      className: "pb-0",
      render: (text: any, values: any) => {
        const {
          creditorNumber,
          creditorName,
          isCreditorActive,
          companyId,
          groupId,
          groupName,
          teleMobile,
          activeCases,
          lastCaseDate,
        } = values ?? {};
        return (
          <>
            <div>
              <$Row gutter={16} className="pb-2">
                <$Col span={8}>
                  <div className="title">
                    <a
                      onClick={(
                        e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
                      ) =>
                        creditorNumber &&
                        rowClick(e, creditorNumber, "creditor")
                      }
                    >
                      <h3 className="mb-0">
                        {creditorNumber}
                        {creditorNumber ? " - " : ""}
                        {creditorName}
                      </h3>
                    </a>
                  </div>
                  <div className="d-flex align-items-center">
                    {isCreditorActive ? (
                      <$Tag className="tag-status-open ">
                        {t("US.COLLECTION.COMMON:COMMON.ACTIVE")}
                      </$Tag>
                    ) : (
                      <$Tag color="tag-status-deactive">
                        {t("US.COLLECTION.COMMON:COMMON.INACTIVE")}
                      </$Tag>
                    )}
                    <small>
                      <label className="ml-2">
                        {t("US.COLLECTION.COMMON:COMMON.COMPANYID")} :{" "}
                      </label>
                      <span>
                        {companyId && companyId}
                        {!companyId && "---"}
                      </span>
                    </small>
                  </div>
                </$Col>
                <$Col span={7}>
                  <div className="d-flex align-items-start">
                    <div className="pr-5">
                      <label>
                        {t("US.COLLECTION.COMMON:COMMON.CREDITORGROUP")}
                      </label>
                      {(groupId || groupName) && (
                        <a
                          onClick={(
                            e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
                          ) =>
                            groupId && rowClick(e, groupId, "creditor-group")
                          }
                        >
                          <div
                            className="flex-fill"
                            data-testid="searchResults-creditorNameAndEntRoleId"
                          >
                            {groupId + " - " + groupName}
                          </div>
                        </a>
                      )}
                      {!groupId && !groupName && "---"}
                    </div>
                  </div>
                </$Col>
                <$Col span={3}>
                  <div className="d-flex align-items-start">
                    <div className="pr-5">
                      <label>{t("US.COLLECTION.COMMON:COMMON.DIRECT")}</label>
                      <div>
                        {teleMobile && teleMobile}
                        {!teleMobile && "---"}
                      </div>
                    </div>
                  </div>
                </$Col>
                <$Col span={3}>
                  <div className="d-flex align-items-start">
                    <div className="pr-5">
                      <label>
                        {t("US.COLLECTION.COMMON:COMMON.ACTIVE_CASES")}
                      </label>
                      <div>{activeCases > 0 ? activeCases : "---"}</div>
                    </div>
                  </div>
                </$Col>
                <$Col span={3}>
                  <div className="d-flex align-items-start">
                    <div className="pr-5">
                      <label>
                        {t("US.COLLECTION.COMMON:COMMON.LAST_CASE_DATE")}
                      </label>
                      <div>
                        {lastCaseDate &&
                          dayjs
                            .utc(lastCaseDate)
                            .isAfter(dayjs.utc(DateValues.minDate)) && (
                            <$DateLabel value={lastCaseDate} />
                          )}
                        {!lastCaseDate && "---"}
                      </div>
                    </div>
                  </div>
                </$Col>
              </$Row>
            </div>
          </>
        );
      },
    },
  ];

  return (
    <ResultView
      rowKey="key"
      currentHit={currentHit}
      dataSource={creditorData}
      paginationChangeEvent={paginationChangeEvent}
      tableClassName="result-table creditor-result-table"
      tableColumns={tableColumns}
      totalSearchResult={totalSearchResult}
    />
  );
};
